<template>
  <div>
    <a-card :bordered="false" title="现金流量表">
      <div class="period-bar">
        <a-button size="small" icon="left" @click="shiftPeriod(-1)" />
        <span class="period-text">{{ periodLabel }}</span>
        <a-button size="small" icon="right" @click="shiftPeriod(1)" />
        <a-month-picker style="margin-left: 12px;" :value="periodMoment" format="YYYY-MM" :allowClear="false" @change="onPeriodChange" />
        <span style="margin-left: 12px; display: inline-block; width: 180px;">
          <store-tenant-select domain="finance" @change="handleStoreTenantChange"></store-tenant-select>
        </span>
        <a-button style="margin-left: 12px;" icon="reload" @click="searchQuery">刷新</a-button>
        <a-button class="float-right" icon="setting" @click="editItems">编辑数据项</a-button>
      </div>
      <a-tabs v-model="activeTab" @change="onTabChange">
        <a-tab-pane tab="主表" key="cashflow" />
        <a-tab-pane v-if="showSuppTab" tab="补充资料" key="cashflow_supp" />
      </a-tabs>
      <a-spin :spinning="loading">
        <div class="scroll-wrap" :style="{ maxHeight: scrollHeight + 'px', overflowY: 'auto' }">
        <table class="cf-table">
          <thead>
            <tr>
              <th>项目</th>
              <th class="cf-rowno">行次</th>
              <th class="cf-amt">本期金额</th>
              <th class="cf-amt">本年累计金额</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(it, idx) in displayRows">
              <tr v-if="it.row.isGroup === '1'" :key="'g' + idx" class="cf-group">
                <td :colspan="4" :style="indentStyle(it.row)">
                  <a-icon v-if="it.row.folded === '1' && it.hasChildren" :type="foldIcon(it.row)" class="fold-toggle" @click="toggleFold(it.row.id)" />
                  <span :class="{ 'cf-bold': it.row.bold === '1' }">{{ it.row.name }}</span>
                </td>
              </tr>
              <tr v-else :key="'r' + it.row.id" :class="{ 'cf-bold': it.row.bold === '1' }">
                <td :style="indentStyle(it.row)">
                  <a-icon v-if="it.row.folded === '1' && it.hasChildren" :type="foldIcon(it.row)" class="fold-toggle" @click="toggleFold(it.row.id)" />
                  {{ it.row.name }}
                </td>
                <td class="cf-rowno">{{ it.row.rowNo }}</td>
                <td class="cf-amt" :class="{ neg: isNeg(it.row.amount1) }">{{ fmtAmount(it.row.amount1) }}</td>
                <td class="cf-amt" :class="{ neg: isNeg(it.row.amount2) }">{{ fmtAmount(it.row.amount2) }}</td>
              </tr>
            </template>
            <tr v-if="!loading && !displayRows.length">
              <td colspan="4" style="text-align: center; color: #999;">暂无数据</td>
            </tr>
          </tbody>
        </table>
        </div>
      </a-spin>
      <div v-if="checks.length" style="margin-top: 12px;">
        <div style="font-weight: 600; margin-bottom: 4px;">勾稽校验</div>
        <div v-for="(c, i) in checks" :key="'ck' + i" style="line-height: 22px;">
          <span :class="c.ok ? 'cf-ok' : 'neg'">{{ c.ok ? '√' : '×' }}</span> {{ c.name }}<template v-if="!c.ok">（预期 {{ fmtCheck(c.expected) }} / 实际 {{ fmtCheck(c.actual) }}）</template>
        </div>
      </div>
      <div v-if="unassignedHint" class="neg" style="margin-top: 8px;">{{ unassignedHint }}
        <a-button size="small" type="primary" style="margin-left: 8px;" :loading="backfilling" @click="backfillTags">补录标签</a-button>
      </div>
      <a-alert v-if="activeTab === 'cashflow'" style="margin-top: 12px;" type="info" show-icon message="归类规则：资金类科目分录通过「现金流」辅助核算项目归类（凭证录入时按对方科目智能推荐，映射规则可在 凭证规则→现金流映射 中维护）；未指定现金流项目的资金分录计入未分配现金流量" />
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'
import { ledgerCashFlow, ledgerCashFlowSupp, accountSetCurrent, cashFlowBackfillTags } from './api'
import StoreTenantSelect from '@/components/StoreTenantSelect'
export default {
  name: 'CashFlowStatementList',
  components: { StoreTenantSelect },
  data() {
    return {
      accountSetId: null,
      period: moment().format('YYYY-MM'),
      activeTab: 'cashflow',
      loading: false,
      scrollHeight: 400,
      standard: '',
      rows: [],
      checks: [],
      unassigned: {},
      backfilling: false,
      expandedKeys: new Set()
    }
  },
  computed: {
    /** 补充资料（间接法）仅企业会计准则预置 */
    showSuppTab() {
      return this.standard === '企业会计准则'
    },
    periodLabel() {
      const m = moment(this.period, 'YYYY-MM')
      if (!this.period || !m.isValid()) return this.period || ''
      return m.format('YYYY') + '年第' + m.format('MM') + '期'
    },
    periodMoment() {
      return this.period ? moment(this.period, 'YYYY-MM') : null
    },
    unassignedHint() {
      if (this.activeTab !== 'cashflow') return ''
      const un = this.unassigned || {}
      const amount = Number(un.amount || 0)
      const count = Number(un.count || 0)
      if (amount === 0 && count === 0) return ''
      return '有 ' + count + ' 笔资金分录未指定现金流项目，合计 ¥' + amount.toFixed(2) + '（现金流量表可能不完整）'
    },
    displayRows() {
      return this.buildDisplay(this.rows)
    }
  },
  created() { this.loadCurrent() },
  mounted() {
    this.$nextTick(() => {
      const el = this.$el.querySelector('.scroll-wrap')
      if (el) {
        const top = el.getBoundingClientRect().top
        this.scrollHeight = Math.max(300, window.innerHeight - top - 24)
      }
    })
  },
  methods: {
    loadCurrent() {
      accountSetCurrent({}).then((res) => {
        if (res && res.success && res.data && res.data.id) {
          this.accountSetId = res.data.id
          this.searchQuery()
        } else { this.$message.warning('尚未建账或未设当前账套') }
      })
    },
    shiftPeriod(dir) {
      this.period = moment(this.period, 'YYYY-MM').add(dir, 'month').format('YYYY-MM')
      this.searchQuery()
    },
    handleStoreTenantChange() {
      this.searchQuery()
    },
    onPeriodChange(m) {
      if (!m) return
      this.period = m.format('YYYY-MM')
      this.searchQuery()
    },
    onTabChange() {
      this.searchQuery()
    },
    searchQuery() {
      if (this.activeTab === 'cashflow_supp') this.fetchReport(ledgerCashFlowSupp, true)
      else this.fetchReport(ledgerCashFlow, false)
    },
    fetchReport(apiFn, isSupp) {
      if (!this.accountSetId || !this.period) return
      this.loading = true
      apiFn({ accountSetId: this.accountSetId, period: this.period }).then((res) => {
        if (res && res.success && res.data) {
          const d = res.data
          if (!isSupp) this.standard = (d.meta && d.meta.standard) || ''
          this.rows = d.rows || []
          this.checks = d.checks || []
          this.unassigned = isSupp ? {} : (d.unassigned || {})
        } else {
          if (res && !res.success && res.message) this.$message.warning(res.message)
          if (!isSupp) this.standard = ''
          this.rows = []
          this.checks = []
          this.unassigned = {}
        }
        this.loading = false
      }).catch(() => { this.loading = false })
    },
    /** 历史凭证现金流标签补录：按映射规则补插标签后重新查询（勾稽应转绿） */
    backfillTags() {
      if (!this.accountSetId) return
      this.$confirm({
        title: '补录现金流标签？',
        content: '将按现金流映射规则为未打标的历史资金分录补插标签（不修改凭证金额/状态/凭证号）。确定继续？',
        onOk: () => {
          this.backfilling = true
          cashFlowBackfillTags({ accountSetId: this.accountSetId }).then((res) => {
            if (res && res.success) {
              this.$message.success(res.message || '补录完成')
              this.searchQuery()
            } else {
              this.$message.warning((res && res.message) || '补录失败')
            }
            this.backfilling = false
          }).catch(() => { this.backfilling = false })
        }
      })
    },
    /** 折叠可见行计算：栈记录未展开折叠行的层级，其后更深连续行隐藏；同时标记各行是否有子级 */
    buildDisplay(rows) {
      const out = []
      const collapsed = []
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        while (collapsed.length && collapsed[collapsed.length - 1] >= row.level) collapsed.pop()
        if (collapsed.length) continue
        const hasChildren = i + 1 < rows.length && rows[i + 1].level > row.level
        out.push({ row: row, hasChildren: hasChildren })
        if (row.folded === '1' && hasChildren && !this.expandedKeys.has(row.id)) collapsed.push(row.level)
      }
      return out
    },
    indentStyle(row) {
      return { paddingLeft: ((row.level || 0) * 15 + 8) + 'px' }
    },
    foldIcon(row) {
      return this.expandedKeys.has(row.id) ? 'down' : 'right'
    },
    toggleFold(id) {
      const keys = new Set(this.expandedKeys)
      if (keys.has(id)) keys.delete(id)
      else keys.add(id)
      this.expandedKeys = keys
    },
    fmtAmount(v) {
      if (v === null || v === undefined || v === '') return ''
      return Number(v).toFixed(2)
    },
    isNeg(v) {
      return v !== null && v !== undefined && v !== '' && Number(v) < 0
    },
    fmtCheck(v) {
      if (v === null || v === undefined || v === '') return '—'
      return Number(v).toFixed(2)
    },
    /** 数据项维护：路径固定走 cashflow 菜单路由，query.type 区分主表/补充资料 */
    editItems() {
      this.$router.push({ path: '/plugin/gledger/reportItem/cashflow', query: { type: this.activeTab, standard: this.standard } })
    }
  }
}
</script>

<style scoped>
.period-bar { margin-bottom: 12px; }
.period-text { display: inline-block; min-width: 110px; text-align: center; font-weight: 600; }
.float-right { float: right; }
.scroll-wrap { overflow-x: auto; }
.cf-table { width: 100%; border-collapse: collapse; }
.cf-table th, .cf-table td { border: 1px solid #e8e8e8; padding: 8px 12px; }
.cf-table th { background: #fafafa; font-weight: 600; }
.cf-rowno { width: 60px; text-align: center; }
.cf-amt { width: 130px; text-align: right; }
.cf-group td { font-weight: 600; background: #fafafa; }
.cf-bold { font-weight: 600; }
.neg { color: #f5222d; }
.cf-ok { color: #52c41a; }
.fold-toggle { margin-right: 4px; color: #1890ff; cursor: pointer; }
</style>
